import React from "react";
import { HeaderProps } from "./types";
import {
  logout,
  getLoggedInUser,
  LoggedInUser,
} from "../../services/authService";
import ConfirmDialog from "../ConfirmDialog";

export default function Header(props: Readonly<HeaderProps>) {
  const [user, setUser] = React.useState<LoggedInUser | null>(null);
  const [loadingUser, setLoadingUser] = React.useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const u = await getLoggedInUser();
      setUser(u);
      setLoadingUser(false);
    })();
  }, []);

  const roleLabel = React.useMemo(() => {
    const r = (user?.customRole || "").toLowerCase();
    if (r === "superadmin" || r === "super admin") return "SUPER ADMIN";
    if (r === "msp") return "MSP";
    return "";
  }, [user]);

  const getTitle = () => `WELCOME ${roleLabel}`;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const initials = React.useMemo(() => {
    const n = user?.name || "";
    const parts = n.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || "").join("");
  }, [user]);

  const userSection = (() => {
    if (loadingUser) {
      return <div className="animate-pulse h-6 w-40 rounded bg-white/20" />;
    }

    if (user) {
      return (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold">
            {initials}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">{user.name}</div>
            {user.email && (
              <div className="text-xs opacity-90">{user.email}</div>
            )}
          </div>
        </div>
      );
    }

    return <div className="text-sm opacity-90">Not signed in</div>;
  })();

  return (
    <>
      <header className="relative h-[70px] bg-gradient-to-r from-[#0E2976] to-[#28418F] px-8 flex items-end justify-between">
        <h1 className="text-white text-[18px] mb-5 tracking-wide">
          {getTitle()}
        </h1>

        <div className="mb-5 flex items-center gap-4 text-white">
          {userSection}

          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="ml-3 flex items-center hover:opacity-80 transition-opacity hover:cursor-pointer"
            title="Logout"
            aria-label="Logout"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-0.5"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </header>

      <ConfirmDialog
        open={showLogoutConfirm}
        message="Are you sure you want to log out?"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </>
  );
}
